import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Input, Row, Select, Switch, message, Modal } from "antd"; // ✅ Added Switch
import { SelectControl } from "../../controls/SelectControl";
import { useBlockScreen, useRoot } from "../../rootContext";
import { fetchData, postData, showError } from "../../utils";

const AddProduct = ({ product = null, onClose = () => { }, onSuccess = () => { } }) => {
  const blocker = useBlockScreen();
  const root = useRoot();

  const [formState, setFormState] = useState({});
  const [selectOptions, setSelectOptions] = useState({ brands: [], categories: [], platforms: [] });
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const supplierNames = ["DandH"];

  const isEdit = !!product;
  const [skuExists, setSkuExists] = useState(false);
  const { user } = root || {}; // ✅ Add this line

  useEffect(() => {
    fetchData(
      window.SiteBasePath + `/api/Products/master-data?types=brands,categories,platforms,suppliers`,
      { blocker, root }
    )
      .then((resp) => {
        if (resp) {
          const apiSuppliers = resp.suppliers || [];
          const defaultSuppliers = [
            "BlueStar Inc.",
            "Ingram Micro",
            "MS Cash Drawer",
            "ScanSource",
            "D&H",
            "Synnex",
            "ASI",
            "Gentek",
            "TDL Canada",
            "ADI",
            "Aartech",
            "Arrow",
            "1tnt",
            "ISPSupplies",
            "Office",
          ];

          // Merge + dedupe (case-insensitive, trimmed)
          const mergedSuppliers = Array.from(
            new Set(
              [...apiSuppliers, ...defaultSuppliers].map((s) => s.trim().toLowerCase())
            )
          )
            .map((s) => {
              // restore proper casing if in defaults, else keep as typed
              const match = defaultSuppliers.find(
                (d) => d.trim().toLowerCase() === s
              );
              return match || s;
            })
            .sort();

          setSelectOptions((prev) => ({
            ...resp,
            suppliers: mergedSuppliers,
          }));
        }


        // initialize form after master data loaded so we can map platform names -> ids
        if (product) {
          const platformsMaster = resp?.platforms || [];
          const mappedPlatforms = (product.Platforms || [])
            .map((p) => {
              if (typeof p === "number") return p;
              if (p && typeof p === "object" && p.Id) return p.Id;
              if (typeof p === "string") {
                const found = platformsMaster.find(
                  (m) => m.Name === p || String(m.Id) === p
                );
                return found ? found.Id : null;
              }
              return null;
            })
            .filter((x) => x != null);

          const mappedProduct = {
            ...product,
            Platforms: mappedPlatforms,
            Suppliers: product.Suppliers || [],
            Active: product.Active ?? true,
          };

          setFormState(mappedProduct);
          form.setFieldsValue(mappedProduct);
        } else {
          setFormState({ SupplierSkus: {}, Suppliers: [], Active: true });
          form.resetFields();
        }
      })
      .catch((err) => console.error(err));
  }, [product]);

  const updateFormState = (name, value) => {
    setFormState((s) => ({ ...s, [name]: value }));
    form.setFieldsValue({ [name]: value });
  };

  const validateSku = (sku) => {
    return /^([a-zA-Z0-9()_+=.\-\s])*$/.test(sku || "");
  };

  const save = async () => {
    try {
      const values = await form.validateFields();

      if (!validateSku(values.Sku)) {
        showError("Sku contains some invalid character");
        return;
      }

      if (!values.Sku && !isEdit) {
        showError("SKU is required.");
        return;
      }

      if (!values.Title || !values.Category || !values.Brand) {
        showError("All fields required.");
        return;
      }

      setLoading(true);

      const platformsValue = values.Platforms || [];
      const platforms = platformsValue
        .map((p) => (typeof p === "number" ? p : p?.value ? p.value : Number(p)))
        .filter((x) => !isNaN(x));

      const payload = {
        Brand: values.Brand,
        Category: values.Category,
        Title: values.Title,
        Active: values.Active ?? true,
        Platforms: platforms,
        SupplierSkus: values.SupplierSkus || {},
        Suppliers: values.Suppliers || [],
      };

      let url;
      if (isEdit) {
        payload.Id = product.Id;
        url = `${window.SiteBasePath}/api/products/${product.Id}`;
      } else {
        if (skuExists) {
          showError("SKU already exists. Please use a unique SKU.");
          return;
        }
        payload.Sku = values.Sku;
        payload.Id = 0;
        url = `${window.SiteBasePath}/api/products`;
      }

      await postData(url, payload, { blocker, root });

      // ✅ Non-blocking success message
      message.success({
        content: "✅ Product saved successfully!",
        duration: 2,
        style: {
          position: "fixed",
          bottom: "20px",
          right: "20px",
        },
      });

      // ✅ Preserve only selected fields
      const preservedValues = {
        Brand: values.Brand,
        Platforms: values.Platforms,
        Active: values.Active ?? true,
      };

      // ✅ Reset form completely
      form.resetFields();

      // ✅ Apply preserved values
      form.setFieldsValue(preservedValues);

      // ✅ Reset React form state as well
      setFormState({
        SupplierSkus: {},
        Suppliers: [],
        ...preservedValues,
      });

      // ✅ Trigger refresh if needed (but don't close modal)
      onSuccess?.();

    } catch (err) {
      const msg = err?.message ? err.message : JSON.stringify(err);
      showError(msg || "Failed to save product");
    } finally {
      setLoading(false);
    }
  };


  const fetchProductBySku = async (sku) => {
    if (!sku || isEdit) return;
    try {
      const result = await fetchData(window.SiteBasePath + `/api/products/sku/${sku}`, { blocker, root });
      if (result && result.Id) {
        setSkuExists(true);
        form.setFields([{ name: "Sku", errors: ["SKU already exists"] }]);
      } else {
        setSkuExists(false);
        form.setFields([{ name: "Sku", errors: [] }]);
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  const selectProps = {
    mode: "multiple",
    style: { width: "100%" },
    value: formState.Platforms || [],
    options: (selectOptions.platforms || []).map((x) => ({ value: x.Id, label: x.Name })),
    onChange: (newValue) => updateFormState("Platforms", newValue),
    placeholder: "Select Platforms...",
    maxTagCount: "responsive",
  };

  return (
    <Row>
      <Col span={24}>
        <h4 style={{ margin: "10px 0" }}>{product ? "Edit Product" : "Add Product"}</h4>
        <Card style={{ marginBottom: "10px" }}>
          <Form
            key={product ? product.Id : 'new'}  // ✅ resets when switching from edit to add
            form={form}
            layout="vertical"
          >

            <Form.Item name="Sku" label="Sku" rules={[{ required: !isEdit, message: "Please enter SKU" }]}>
              <Input
                              //to make textbox enable or disable
                              disabled={isEdit}
                              //enable={isEdit}
                onChange={(e) => {
                  let val = e.target.value || "";
                  val = val.replace(/[^a-zA-Z0-9()_+=.\-\s]/g, "");
                  if (skuExists) {
                    setSkuExists(false);
                    form.setFields([{ name: "Sku", errors: [] }]);
                  }
                  updateFormState("Sku", val);
                }}
                onBlur={() => fetchProductBySku(form.getFieldValue("Sku"))}
              />
            </Form.Item>


            <Form.Item
              name="Brand"
              label="Brand"
              rules={[{ required: true, message: "Please select brand" }]}
            >
              <Select
                mode="tags"
                style={{ width: "100%" }}
                value={formState.Brand ? [formState.Brand] : []}
                options={
                  (selectOptions.brands || [])
                    .slice()
                    .sort((a, b) => a.localeCompare(b))
                    .map((x) => ({
                      value: x,
                      label: x,
                    }))
                }
                // ✅ Triggered when user selects from dropdown (correct event)
                onSelect={(val) => {
                  const existing = selectOptions.brands || [];
                  if (!existing.includes(val)) {
                    setSelectOptions((prev) => ({
                      ...prev,
                      brands: [...prev.brands, val].sort((a, b) => a.localeCompare(b)),
                    }));
                  }
                  updateFormState("Brand", val);
                }}
                // ✅ Triggered when user manually types and presses Enter (new tag)
                onChange={(values) => {
                  const val = values[values.length - 1];
                  const existing = selectOptions.brands || [];
                  if (!existing.includes(val)) {
                    setSelectOptions((prev) => ({
                      ...prev,
                      brands: [...prev.brands, val].sort((a, b) => a.localeCompare(b)),
                    }));
                  }
                  updateFormState("Brand", val);
                }}
                onBlur={() => {
                  const input = document.querySelector(".ant-select-selection-search-input");
                  if (input && input.value) input.value = "";
                }}
                placeholder="Select or type brand"
              />
            </Form.Item>




            <Form.Item
              name="Category"
              label="Category"
              rules={[{ required: true, message: "Please select category" }]}
            >
              <Select
                mode="tags"
                style={{ width: "100%" }}
                value={formState.Category ? [formState.Category] : []}
                options={
                  (selectOptions.categories || [])
                    .slice() // clone to avoid mutating the original array
                    .sort((a, b) => a.localeCompare(b)) // ✅ alphabetical sort
                    .map((x) => ({
                      value: x,
                      label: x,
                    }))
                }
                // ✅ Triggered only when user selects from dropdown
                onSelect={(val) => {
                  const existing = selectOptions.categories || [];
                  if (!existing.includes(val)) {
                    setSelectOptions((prev) => ({
                      ...prev,
                      categories: [...prev.categories, val].sort((a, b) => a.localeCompare(b)),
                    }));
                  }
                  updateFormState("Category", val);
                }}
                // ✅ Triggered when user manually types and presses Enter
                onChange={(values) => {
                  const val = values[values.length - 1];
                  const existing = selectOptions.categories || [];

                  if (!existing.includes(val)) {
                    setSelectOptions((prev) => ({
                      ...prev,
                      categories: [...prev.categories, val].sort((a, b) => a.localeCompare(b)),
                    }));
                  }

                  updateFormState("Category", val);
                }}
                onBlur={() => {
                  // ✅ Clear any unfinished typed input
                  const input = document.querySelector(".ant-select-selection-search-input");
                  if (input && input.value) input.value = "";
                }}
                placeholder="Select or type category"
              />
            </Form.Item>



            <Form.Item name="Title" label="Title" rules={[{ required: true, message: "Please enter title" }]}>
              <Input onChange={(e) => updateFormState("Title", e.target.value)} />
            </Form.Item>

            {/* ✅ Active / Inactive Switch */}
            <Form.Item name="Active" label="Status" valuePropName="checked" initialValue={true}>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                onChange={(checked) => updateFormState("Active", checked)}
              />
            </Form.Item>


            <Form.Item label="Platforms" name="Platforms">
              <Select {...selectProps}
                onSelect={() => document.activeElement.blur()}
              />
            </Form.Item>

            <Form.Item name="Suppliers" label="Suppliers">
              <Select
                mode={user?.permissions?.manageSuppliers ? "tags" : "multiple"}
                style={{ width: "100%" }}
                value={formState.Suppliers || []}
                options={
                  (selectOptions.suppliers || [])
                    .slice() // clone to avoid mutating original array
                    .sort((a, b) => a.localeCompare(b)) // ✅ alphabetical sort
                    .map((x) => ({
                      value: x,
                      label: x,
                    }))
                }
                onChange={(newValues) => {
                  const existing = selectOptions.suppliers || [];
                  const newOnes = newValues.filter((val) => !existing.includes(val));

                  if (newOnes.length && user?.permissions?.manageSuppliers) {
                    // ✅ Admins can add new suppliers — keep sorted
                    setSelectOptions((prev) => ({
                      ...prev,
                      suppliers: [...existing, ...newOnes].sort((a, b) => a.localeCompare(b)),
                    }));
                  } else if (newOnes.length && !user?.permissions?.manageSuppliers) {
                    // ❌ Non-admins — block new supplier entries
                    message.warning("You don't have permission to add new suppliers.");
                    const allowedValues = newValues.filter((val) => existing.includes(val));
                    form.setFieldsValue({ Suppliers: allowedValues });
                    updateFormState("Suppliers", allowedValues);
                    return;
                  }

                  updateFormState("Suppliers", newValues);
                }}
                onSelect={() => {
                  // ✅ Prevent half-typed supplier names from being added
                  const input = document.querySelector(".ant-select-selection-search-input");
                  if (input) input.value = "";
                }}
                onBlur={() => {
                  // ✅ Also clear any unfinished typed text on blur
                  const input = document.querySelector(".ant-select-selection-search-input");
                  if (input && input.value) input.value = "";
                }}
                placeholder="Select supplier(s)..."
                maxTagCount="responsive"
              />
            </Form.Item>






            {supplierNames.map((supplier) => (
              <Form.Item label={`${supplier} SKU`} key={supplier} name={["SupplierSkus", supplier]}>
                <Input
                  onChange={(e) => {
                    const value = e.target.value;
                    const cur = form.getFieldValue("SupplierSkus") || {};
                    form.setFieldsValue({ SupplierSkus: { ...cur, [supplier]: value } });
                    setFormState((s) => ({ ...s, SupplierSkus: { ...(s.SupplierSkus || {}), [supplier]: value } }));
                  }}
                />
              </Form.Item>
            ))}

            <div style={{ textAlign: "right" }}>
              <Button onClick={onClose} style={{ marginRight: 8 }}>Cancel</Button>
              <Button type="primary" loading={loading} onClick={save}>Save</Button>
            </div>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default AddProduct;
